/**
 * Porting a spread between wheel styles (SPEC.md §5).
 *
 * Never automatic. The two wheels share a felt but not a ring, so the felt-side
 * bets mostly survive while the thing the user actually cares about — where the
 * coverage sits on the physical wheel — is destroyed and rebuilt somewhere
 * else. This module produces the evidence for that, so the port screen can show
 * it rather than assert it.
 */

import type {
  PlacedBet,
  RingGap,
  Spread,
  SpreadAnalysis,
  TableProfile,
  WheelStyle,
} from './types';
import { SECTOR_PRESETS, betLabel, decodeTarget, identifySectors, validateBet } from './bets';
import { analyseSpread, createSpread } from './spread';

export interface DroppedBet {
  readonly bet: PlacedBet;
  readonly reason: string;
}

export interface GapProfile {
  readonly gaps: readonly RingGap[];
  readonly longest: number;
  readonly count: number;
  readonly pocketsCovered: number;
  readonly pocketCount: number;
}

export interface PortReport {
  readonly from: WheelStyle;
  readonly to: WheelStyle;
  /** The spread as it stands, so the report can render both sides. */
  readonly original: readonly PlacedBet[];
  /** Bets that are legal on the target felt. */
  readonly carried: readonly PlacedBet[];
  readonly dropped: readonly DroppedBet[];
  readonly chipsCarried: number;
  readonly chipsDropped: number;
  readonly before: SpreadAnalysis;
  readonly after: SpreadAnalysis;
  readonly beforeGaps: GapProfile;
  readonly afterGaps: GapProfile;
  /** French call bets present in the source. Single-zero only, so always lost. */
  readonly sectorsLost: readonly string[];
  /**
   * True whenever anything was staked. Physical neighbour relationships never
   * survive a port — the wheels have entirely different sequences — so this is
   * not conditional on which bets happened to carry.
   */
  readonly neighboursBroken: boolean;
}

/**
 * Why a bet cannot exist on the target felt.
 *
 * Every bet among 1–36 is legal on both felts — the number grid is identical.
 * Only bets touching a zero, and the basket, differ, so the reasons are
 * specific rather than a generic "invalid".
 */
function reasonFor(bet: PlacedBet, to: WheelStyle): string {
  const pockets = decodeTarget(bet.target);

  // Most specific first. The basket contains 00, so a plain "no 00 here" check
  // would shadow the more useful reason.
  if (bet.betType === 'basket') {
    return 'The five-number basket exists on the double-zero felt only';
  }
  if (pockets.includes('00') && to === 'single-zero') {
    return 'The 00 pocket does not exist on a single-zero wheel';
  }
  if (bet.betType === 'corner' && pockets.includes('0') && to === 'double-zero') {
    return 'The first four (0-1-2-3) exists on the single-zero felt only';
  }
  if (pockets.includes('0')) {
    return to === 'double-zero'
      ? 'The double-zero felt splits its head column between 0 and 00, so this zero bet has no equivalent'
      : 'The single-zero felt has one zero spanning all three rows, so this zero bet has no equivalent';
  }
  const validation = validateBet(bet, to);
  return validation.ok ? 'Not available on the target felt' : validation.reason;
}

function gapProfile(analysis: SpreadAnalysis): GapProfile {
  return {
    gaps: analysis.ringGaps,
    longest: analysis.longestGap?.length ?? 0,
    count: analysis.ringGaps.length,
    pocketsCovered: analysis.pocketsCovered,
    pocketCount: analysis.wheel.pocketCount,
  };
}

/**
 * Work out what a port would do, without doing it. The caller decides whether
 * to go ahead, and creates the new spread with `applyPort`.
 */
export function planPort(
  bets: readonly PlacedBet[],
  from: WheelStyle,
  to: WheelStyle,
  tables?: { from?: TableProfile; to?: TableProfile },
): PortReport {
  const carried: PlacedBet[] = [];
  const dropped: DroppedBet[] = [];

  for (const bet of bets) {
    if (validateBet(bet, to).ok) carried.push({ ...bet });
    else dropped.push({ bet, reason: reasonFor(bet, to) });
  }

  const before = analyseSpread(bets, from, tables?.from);
  const after = analyseSpread(carried, to, tables?.to);

  return {
    from,
    to,
    original: bets.map((bet) => ({ ...bet })),
    carried,
    dropped,
    chipsCarried: carried.reduce((sum, bet) => sum + bet.chips, 0),
    chipsDropped: dropped.reduce((sum, entry) => sum + entry.bet.chips, 0),
    before,
    after,
    beforeGaps: gapProfile(before),
    afterGaps: gapProfile(after),
    sectorsLost:
      from === 'single-zero'
        ? identifySectors(bets).map((found) => found.preset.name)
        : [],
    neighboursBroken: bets.length > 0,
  };
}

export function planPortFor(spread: Spread, to: WheelStyle, tables?: { from?: TableProfile; to?: TableProfile }): PortReport {
  return planPort(spread.bets, spread.wheelStyle, to, tables);
}

/**
 * Build the ported spread. A new spread with a new id — porting never mutates
 * the original, and `wheelStyle` is immutable after creation (§2.3).
 */
export function applyPort(source: Spread, report: PortReport): Spread {
  const ported = createSpread(`${source.name} (${report.to === 'single-zero' ? 'single' : 'double'} zero)`, report.to, [
    ...report.carried,
  ]);
  ported.tags = [...source.tags];
  return ported;
}

/** Names of the French call bets, for the port screen to explain what is lost. */
export const FRENCH_CALL_BET_NAMES: readonly string[] = SECTOR_PRESETS.map(
  (preset) => preset.name,
);

/** One-line summaries of dropped bets, for the report list. */
export function describeDropped(dropped: readonly DroppedBet[]): string[] {
  return dropped.map((entry) => `${betLabel(entry.bet)} (${entry.bet.chips}) — ${entry.reason}`);
}
